import { useEffect, useState } from 'react'
import CompanySelector from './components/CompanySelector'
import YearSelector from './components/YearSelector'
import CategoryTabs from './components/CategoryTabs'
import SourcesPanorama from './components/SourcesPanorama'
import SyntheseModal from './components/SyntheseModal'
import RichText from './components/RichText'
import LLMRadarChart from './components/RadarChart'
import Login from './components/Login'
import AuthService from './services/AuthService'
import Admin from './Admin.jsx'
import { normalizeReport } from './lib/reportUtils'
import './styles/globals.css'

export default function App() {
  const [user, setUser] = useState(null)
  const [reports, setReports] = useState({})
  const [selectedCompany, setSelectedCompany] = useState(null)
  const [selectedDate, setSelectedDate] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isAdminPage, setIsAdminPage] = useState(false)
  const [activeDetailTab, setActiveDetailTab] = useState('themes')
  const [syntheseOpen, setSyntheseOpen] = useState(false)

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Check cached user first — avoids loading flash on fresh visits
        const cachedUser = AuthService.getCurrentUser()
        if (!cachedUser) {
          setLoading(false)
          return
        }
        // Validate cookie with server
        const validatedUser = await AuthService.restoreSession()
        if (validatedUser) {
          setUser(validatedUser)
          await loadReports(validatedUser)
        } else {
          setLoading(false)
        }
      } catch (error) {
        console.error('Erreur initialisation:', error)
        setLoading(false)
      }
    }
    initializeAuth()
  }, [])

  const loadReports = async (currentUser) => {
    try {
      setLoading(true)
      const organizedData = AuthService.getOrganizedData()

      if (Object.keys(organizedData).length === 0) {
        await AuthService.refreshData()
        const refreshedData = AuthService.getOrganizedData()
        if (Object.keys(refreshedData).length > 0) {
          await loadReportsFromServer(currentUser, refreshedData)
        } else {
          if (!AuthService.isAuthenticated()) {
            setUser(null)
            setLoading(false)
            return
          }
          setReports({})
        }
      } else {
        await loadReportsFromServer(currentUser, organizedData)
      }
      setLoading(false)
    } catch (err) {
      console.error('Erreur chargement:', err)
      setError(err.message)
      setLoading(false)
    }
  }

  const loadReportsFromServer = async (currentUser, organizedData) => {
    try {
      const authorizedFiles = AuthService.getAuthorizedFiles()
      const allReports = {}

      await Promise.all(
        authorizedFiles
          .filter(filename =>
            filename.endsWith('.json') &&
            !filename.includes('_detailed_') &&
            !filename.includes('_synthese') &&
            filename !== 'index.json'
          )
          .map(async (filename) => {
            try {
              const report = await AuthService.fetchAnalysisFile(filename)
              allReports[filename] = normalizeReport(report)
            } catch (err) {
              console.warn(`Skip file ${filename}`, err.message)
            }
          })
      )

      const organizedReports = {}

      Object.entries(organizedData).forEach(([companyName, dateStructure]) => {
        organizedReports[companyName] = {}
        Object.entries(dateStructure).forEach(([date, fileInfo]) => {
          const filename = fileInfo.filename
          if (allReports[filename]) {
            organizedReports[companyName][date] = allReports[filename]
          }
        })
      })

      setReports(organizedReports)
    } catch (error) {
      console.error('Erreur chargement rapports:', error)
      throw error
    }
  }

  useEffect(() => {
    if (Object.keys(reports).length > 0 && !selectedCompany) {
      const firstCompany = Object.keys(reports)[0]
      setSelectedCompany(firstCompany)
      const dates = getAvailableDatesForCompany(firstCompany)
      if (dates.length > 0) setSelectedDate(dates[0])
    }
  }, [reports, selectedCompany, selectedDate])

  // Switching report closes the modal: without this, `syntheseOpen` would stay
  // true while the guard unmounts it, and reappear on the next report that does
  // have a detailed synthesis.
  useEffect(() => {
    setSyntheseOpen(false)
  }, [selectedCompany, selectedDate])

  const getUserCompaniesForUI = () => {
    const organizedData = AuthService.getOrganizedData()
    return Object.keys(organizedData).map(companyName => ({
      name: companyName,
      dates: Object.keys(organizedData[companyName] || {})
    }))
  }

  const handleCompanyChange = (newCompany) => {
    setSelectedCompany(newCompany)
    const dates = getAvailableDatesForCompany(newCompany)
    if (dates.length > 0) setSelectedDate(dates[0])
  }

  const getAvailableDatesForCompany = (companyName) => {
    if (!companyName) return []
    const organizedData = AuthService.getOrganizedData()
    const companyData = organizedData[companyName]
    if (!companyData) return []
    return Object.keys(companyData).sort((a, b) => {
      if (a === 'default') return 1
      if (b === 'default') return -1
      const [monthA, yearA] = a.split('/')
      const [monthB, yearB] = b.split('/')
      if (yearA !== yearB) return parseInt(yearB) - parseInt(yearA)
      return parseInt(monthB) - parseInt(monthA)
    })
  }

  const handleLogin = async (userData) => {
    setUser(userData)
    setLoading(true)
    try {
      await loadReports(userData)
    } catch (error) {
      console.error('Erreur post-login:', error)
      setError('Erreur lors du chargement des données')
      setLoading(false)
    }
  }

  const handleLogout = async () => {
    await AuthService.logout()
    setUser(null)
    setReports({})
    setSelectedCompany(null)
    setSelectedDate(null)
    setError(null)
    setIsAdminPage(false)
  }

  if (!user) return <Login onLogin={handleLogin} />
  if (isAdminPage) return <Admin onBack={() => setIsAdminPage(false)} />

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#d1d6d8' }}>
        <div className="text-center space-y-4">
          <div className="w-10 h-10 border-2 border-[#2f3126] border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-sm font-semibold text-[#77726e]">Chargement des analyses...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#d1d6d8' }}>
        <div className="bg-white border border-[#77726e]/30 shadow-md p-8 max-w-md text-center">
          <p className="text-base font-bold text-[#2f3126] mb-2">Erreur de chargement</p>
          <p className="text-sm text-[#77726e] mb-5">{error}</p>
          <button onClick={handleLogout} className="px-4 py-2 rounded-md bg-[#2f3126] text-[#f6f3ee] text-sm font-semibold hover:bg-[#77726e] transition">
            Retour
          </button>
        </div>
      </div>
    )
  }

  const companiesForUI = getUserCompaniesForUI()
  const hasReports = companiesForUI.length > 0 && Object.keys(reports).length > 0

  if (!hasReports || !selectedCompany || !selectedDate) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#d1d6d8' }}>
        <div className="bg-white border border-[#77726e]/30 shadow-md p-8 max-w-md text-center">
          <p className="text-base font-bold text-[#2f3126] mb-2">Aucun rapport disponible</p>
          <p className="text-sm text-[#77726e] mb-2">
            {user.role === 'admin'
              ? 'Aucune donnée trouvée dans le système.'
              : `Aucune analyse assignée à votre compte (${user.company}).`}
          </p>
          {user.role !== 'admin' && (
            <p className="text-xs text-[#77726e] mb-5">Contactez votre administrateur pour obtenir l'accès.</p>
          )}
          <button onClick={handleLogout} className="px-4 py-2 rounded-md bg-[#2f3126] text-[#f6f3ee] text-sm font-semibold hover:bg-[#77726e] transition">
            Déconnexion
          </button>
        </div>
      </div>
    )
  }

  const report = reports[selectedCompany]?.[selectedDate]
  const availableDates = getAvailableDatesForCompany(selectedCompany)

  // Synthesis fields are all optional: an audit may carry none of them (pipeline
  // interrupted before phase 3), only the long `synthese` (audits predating the
  // preview), or both. Blank strings count as absent.
  const text = (v) => (typeof v === 'string' ? v.trim() : '')
  const syntheseFull = text(report?.synthese)
  const syntheseResume = text(report?.synthese_resume)
  const syntheseEvolution = text(report?.synthese_evolution)
  const synthesePreview = syntheseResume || syntheseFull
  // Nothing to open when the preview already *is* the full text.
  const hasFullSynthese = Boolean(syntheseFull && syntheseResume && syntheseFull !== syntheseResume)

  if (!report || !Array.isArray(report.llm_results)) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#d1d6d8' }}>
        <div className="bg-white border border-[#77726e]/30 shadow-md p-8 max-w-md text-center">
          <p className="text-base font-bold text-[#2f3126] mb-2">Rapport invalide</p>
          <p className="text-sm text-[#77726e] mb-5">Le rapport sélectionné est incomplet ou corrompu.</p>
          <button onClick={() => window.location.reload()} className="px-4 py-2 rounded-md bg-[#2f3126] text-[#f6f3ee] text-sm font-semibold hover:bg-[#77726e] transition">
            Recharger
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#d1d6d8' }}>

      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-[#77726e]/20" style={{ backgroundColor: '#d1d6d8' }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">

          <div className="flex items-center gap-6">
            <div className="flex items-baseline gap-2">
              <h1 className="text-3xl font-extrabold text-[#2f3126] font-title tracking-tight leading-none">
                ReputAI
              </h1>
              {/* "by" and the logo move as one block: they keep their own centred
                  relationship, and the block's baseline (that of "by", its first item)
                  drops onto the title's baseline */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-[#77726e] leading-none">by</span>
                {/* the asset carries ~8% transparent padding on its left edge: pulled back
                    so the gap after "by" matches the one before it */}
                <a
                  href="https://opsci.ai"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center -ml-1.5"
                >
                  <img
                    src="./opsci-logo-hg.png"
                    alt="OpSci"
                    className="h-5 opacity-70 hover:opacity-100 transition"
                  />
                </a>
              </div>
            </div>
            {companiesForUI.length > 1 && (
              <CompanySelector
                companies={companiesForUI.map(c => c.name)}
                selected={selectedCompany}
                onChange={handleCompanyChange}
              />
            )}
          </div>

          <div className="flex items-center gap-3">
            <YearSelector
              years={availableDates.map(date => ({
                value: date,
                label: AuthService.formatDateForDisplay(date)
              }))}
              selected={selectedDate}
              onChange={(newDate) => {
                setSelectedDate(newDate)
              }}
            />
            {user.role === 'admin' && (
              <button
                onClick={() => setIsAdminPage(true)}
                className="px-3 py-1.5 rounded-md border border-[#4e6498]/40 text-xs font-semibold text-[#4e6498] bg-[#f6f3ee] hover:bg-[#d1d6d8] transition"
              >
                Admin
              </button>
            )}
            {user.logo && (
              /* Squared off against the "ReputAI" title on the other side: 30px is that
                 h1's line box (text-3xl + leading-none), and the width is capped at three
                 times it. A wordmark wider than 3:1 is fitted to the width and so renders
                 shorter than the title. */
              <img
                src={`/api/logo/${encodeURIComponent(user.username)}`}
                alt={user.company || ''}
                className="h-[30px] max-w-[90px] object-contain"
              />
            )}
            <button
              onClick={handleLogout}
              className="px-3 py-1.5 rounded-md bg-[#2f3126] text-[#f6f3ee] text-xs font-semibold hover:bg-[#77726e] transition"
            >
              Déconnexion
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-6">

        {/* Score global + Radar */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">

          {/* Radar (3/5) */}
          <div className="lg:col-span-3">
            {report?.llm_results?.length > 0
              ? <LLMRadarChart report={report} />
              : (
                <div className="audit-card p-6 h-full flex items-center justify-center text-sm text-[#77726e]">
                  Données insuffisantes pour le graphique radar
                </div>
              )
            }
          </div>

          {/* Score global (2/5) */}
          <div className="lg:col-span-2">
            <div className="audit-card p-6 h-full flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-bold text-[#2f3126] uppercase tracking-wider flex items-center gap-2">
                  <span className="inline-block w-1.5 h-5 bg-[#77726e]" />
                  Score Global
                </h2>
                <span className="text-3xl font-extrabold text-[#2f3126] font-title">
                  {report.global_score || 0}/100
                </span>
              </div>

              <div className="w-full bg-[#d1d6d8] h-2.5 mb-1">
                <div
                  className="h-full transition-all duration-700"
                  style={{ width: `${report.global_score || 0}%`, backgroundColor: '#4e6498' }}
                />
              </div>

              <div className="mt-5 pt-4 border-t border-[#77726e]/20 flex-1">
                <h3 className="text-xs font-bold text-[#77726e] uppercase tracking-wider mb-2">
                  En synthèse
                </h3>
                {synthesePreview
                  ? <RichText text={synthesePreview} size="text-xs" />
                  : <p className="text-xs text-[#2f3126] leading-relaxed">Aucune synthèse disponible.</p>
                }
                {syntheseEvolution && (
                  <RichText text={syntheseEvolution} size="text-xs" className="mt-3" />
                )}
                {hasFullSynthese && (
                  <button
                    onClick={() => setSyntheseOpen(true)}
                    className="mt-3 text-xs font-semibold text-[#4e6498] hover:underline"
                  >
                    Lire la synthèse détaillée →
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Analyse détaillée */}
        <div className="audit-card overflow-hidden">
          <div className="flex border-b border-[#77726e]/15">
            {[
              { key: 'themes', label: 'Analyse détaillée par thématique et par modèle' },
              { key: 'sources', label: 'Panorama des sources' },
            ].map((tab, i) => (
              <div
                key={tab.key}
                onClick={() => setActiveDetailTab(tab.key)}
                className={`flex-1 px-6 pt-5 pb-4 flex items-center gap-2 cursor-pointer transition select-none ${
                  i === 0 ? 'border-r border-[#77726e]/15' : ''
                } ${
                  activeDetailTab === tab.key
                    ? 'bg-white'
                    : 'bg-[#f6f3ee] hover:bg-[#edeae4]'
                }`}
              >
                <span className={`inline-block w-1.5 h-5 shrink-0 ${activeDetailTab === tab.key ? 'bg-[#77726e]' : 'bg-[#77726e]/40'}`} />
                <h3 className={`text-sm font-bold uppercase tracking-wider ${activeDetailTab === tab.key ? 'text-[#2f3126]' : 'text-[#77726e]'}`}>
                  {tab.label}
                </h3>
              </div>
            ))}
          </div>
          <div className="p-6">
            {report?.llm_results?.length > 0
              ? activeDetailTab === 'themes'
                ? <CategoryTabs report={report} />
                : <SourcesPanorama report={report} />
              : (
                <div className="text-center py-8 text-sm text-[#77726e]">
                  Aucune donnée disponible.
                </div>
              )
            }
          </div>
        </div>
      </main>

      {syntheseOpen && hasFullSynthese && (
        <SyntheseModal
          company={report.company || selectedCompany}
          synthese={syntheseFull}
          evolution={syntheseEvolution}
          onClose={() => setSyntheseOpen(false)}
        />
      )}

      <footer className="border-t border-[#77726e]/20 mt-12">
        <div className="max-w-7xl mx-auto px-6 py-5 flex justify-center">
          <a href="https://opsci.ai" target="_blank" rel="noopener noreferrer">
            <img src="./opsci-logo-hg.png" alt="OpSci" className="h-7 opacity-50 hover:opacity-80 transition" />
          </a>
        </div>
      </footer>

    </div>
  )
}
